import { useEffect, useRef, useState } from "react";
import { content, type Locale } from "./content";

const lessons = [
  {
    id: "im-in-a-hurry-0aba437c7a",
    phrase: "I'm in a hurry.",
    duration: "0:47",
  },
  {
    id: "im-stressed-de5cb90ad0",
    phrase: "I'm stressed out.",
    duration: "0:53",
  },
  {
    id: "make-a-decision-5542f5eb53",
    phrase: "Make a decision.",
    duration: "0:56",
  },
] as const;

function LessonVideo({
  lesson,
  index,
  locale,
  onPlay,
}: {
  lesson: (typeof lessons)[number];
  index: number;
  locale: Locale;
  onPlay: (video: HTMLVideoElement) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);
  const t = content[locale].lessons;
  const src = `/lessons/${lesson.id}.mp4`;

  const play = () => {
    const player = video.current;
    if (!player) return;
    if (failed) player.load();
    setFailed(false);
    setStarted(true);
    // Keep play() in the click handler so mobile browsers allow sound.
    void player
      .play()
      .then(() => {
        if (!player.paused) player.focus({ preventScroll: true });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setStarted(false);
        setFailed(true);
      });
  };

  return (
    <article className="lesson-card" aria-labelledby={`lesson-${index}`}>
      <div className="lesson-media">
        <video
          ref={video}
          src={src}
          poster={`/lessons/${lesson.id}.webp`}
          width="1080"
          height="1920"
          playsInline
          tabIndex={started ? 0 : -1}
          preload="none"
          controls={started && !failed}
          aria-label={`${t.watch}: ${lesson.phrase}`}
          onPlay={(event) => onPlay(event.currentTarget)}
          onError={() => {
            setFailed(true);
            setStarted(false);
          }}
        />
        {!started && !failed && (
          <button
            className="lesson-play"
            type="button"
            onClick={play}
            aria-label={`${t.watch}: ${lesson.phrase}`}
          >
            <span className="lesson-play-icon" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="m9 5 11 7-11 7V5Z" />
              </svg>
            </span>
            <span>{t.watch}</span>
          </button>
        )}
        {failed && (
          <div className="lesson-error" role="status">
            <p>{t.error}</p>
            <button
              className="button button-primary"
              type="button"
              onClick={play}
            >
              {t.retry}
            </button>
            <a href={src}>{t.open}</a>
          </div>
        )}
      </div>
      <div className="lesson-copy">
        <div className="lesson-meta">
          <span>{t.categories[index]}</span>
          <span dir="ltr">{lesson.duration}</span>
        </div>
        <h3 id={`lesson-${index}`} lang="en" dir="ltr">
          {lesson.phrase}
        </h3>
        <p>{t.descriptions[index]}</p>
      </div>
    </article>
  );
}

export function LessonVideos({ locale }: { locale: Locale }) {
  const group = useRef<HTMLDivElement>(null);
  const t = content[locale].lessons;

  const pauseOthers = (playing: HTMLVideoElement) => {
    group.current?.querySelectorAll("video").forEach((video) => {
      if (video !== playing) video.pause();
    });
  };

  useEffect(() => {
    const pauseHidden = () => {
      if (document.hidden)
        group.current
          ?.querySelectorAll("video")
          .forEach((video) => video.pause());
    };
    document.addEventListener("visibilitychange", pauseHidden);
    return () => document.removeEventListener("visibilitychange", pauseHidden);
  }, []);

  return (
    <section
      className="section container lessons"
      id="lessons"
      aria-labelledby="lessons-title"
    >
      <div className="section-heading">
        <div>
          <span className="eyebrow">
            <span />
            {t.label}
          </span>
          <h2 id="lessons-title">{t.title}</h2>
        </div>
        <p>{t.body}</p>
      </div>
      <div className="lesson-grid" ref={group}>
        {lessons.map((lesson, index) => (
          <LessonVideo
            key={lesson.id}
            lesson={lesson}
            index={index}
            locale={locale}
            onPlay={pauseOthers}
          />
        ))}
      </div>
      <div className="lessons-footer">
        <p>{t.note}</p>
        <a className="button button-ghost" href="#method">
          {t.next}
          <span aria-hidden="true">↓</span>
        </a>
      </div>
    </section>
  );
}
